import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { PlanResolver } from '../resolvers';
import { PolicyService } from '../services';
import { Policy } from '@wsphere/warranties/domain';

@Injectable()
export class PlanGuard implements CanActivate {
  constructor(
    private resolver: PlanResolver,
    private policySvc: PolicyService,
    private router: Router
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const previousPlan = this.policySvc.getPlanSnapshot();
    this.resolver.plan = null;

    const policyId = this.findPolicyId(route);
    if (!policyId) return false;

    const planId = this.findPlanId(route);
    if (!planId || planId === 'undefined') {
      const fallback = await this.resolvePlanFallback(policyId, planId, previousPlan);
      if (fallback) return fallback;
      return false;
    }

    this.resolver.plan = await this.policySvc.getPlan(policyId, planId, {
      includeCoverageConfigs: false,
      includeFees: false,
      includeCoverageConfigBlocks: false,
    });
    if (!this.resolver.plan) {
      const fallback = await this.resolvePlanFallback(policyId, planId, previousPlan);
      if (fallback) return fallback;
      return false;
    }

    // Broadcast the resolved plan so downstream components render the correct version
    this.policySvc.setPlan(this.resolver.plan);

    return true;
  }

  private findPolicyId(route: ActivatedRouteSnapshot): string {
    return route.params['policyId'] || (route.parent ? this.findPolicyId(route.parent) : null);
  }

  private findPlanId(route: ActivatedRouteSnapshot): string {
    return route.params['planId'] || (route.parent ? this.findPlanId(route.parent) : null);
  }

  private async resolvePlanFallback(
    policyId: string,
    planId: string,
    previousPlan: Policy.Plan.Model | null
  ): Promise<UrlTree | null> {
    // If the specific plan id isn't found (e.g., after auto-versioning), try to locate a replacement plan
    const policy = await this.policySvc.getOne(policyId);
    const planIdBase = planId?.split('-')[0] || '';
    const targetTitle = previousPlan?.title?.trim().toLowerCase();
    const targetOrder = previousPlan?.order;

    const candidate =
      policy?.plans?.find(p => p.id === planId) ||
      (targetTitle ? policy?.plans?.find(p => p.title?.trim().toLowerCase() === targetTitle) : null) ||
      (targetOrder !== undefined ? policy?.plans?.find(p => p.order === targetOrder) : null) ||
      (planIdBase ? policy?.plans?.find(p => p.id.startsWith(planIdBase)) : null) ||
      (policy?.plans?.length ? policy.plans[0] : null);

    if (candidate?.id) {
      return this.router.parseUrl(`/policies/${policyId}/plans/${candidate.id}`);
    }

    return null;
  }
}
